import { generateId } from 'util/generateId';
import { useCollection, CollectionDispatch, useLocalStorageCollection, useSessionStorageCollection } from 'hooks/useCollection';
import { useCallback, useMemo } from 'react';

export type Entry<T> = Readonly<{ key: string; value: T }>;

function isEntry<T>(value: unknown): value is Entry<T> {
  return (typeof (value as any)?.key) === 'string';
}

function isEntryArray<T>(value: unknown): value is Entry<T>[] {
  return Array.isArray(value) && !!value.length && isEntry(value[0]);
}

function createEntry<T>(value: T) {
  return { key: generateId(), value };
}

type KeyedCollectionDispatch<T> = {
  add: (...items: T[]) => void;
  get: (arg: string | number | Entry<T>) => T | undefined;
  put: (key: string, value: T) => void;
  remove: (arg: number | string | Entry<T>) => void;
  clear: () => void;
};

function useKeyedCollectionDispatch<T>(items: Entry<T>[], collection: CollectionDispatch<Entry<T>>): KeyedCollectionDispatch<T> {
  const entryLookupMap = useMemo(() => items.reduce((map, entry, index) => {
    // eslint-disable-next-line functional/immutable-data
    map[entry.key] = index;

    return map;
  }, {} as Record<string, number>), [items]);

  const getIndex = useCallback<(arg: string | number | Entry<T>) => number>(arg => {
    switch (typeof arg) {
      case 'number':
        return Math.max(-1, arg);
      case 'string':
        return entryLookupMap[arg] ?? -1;
      default:
        return entryLookupMap[arg.key] ?? -1;
    }
  }, [entryLookupMap]);

  return useMemo<KeyedCollectionDispatch<T>>(() => {
    return {
      add(...addedItems) { return collection.add(...addedItems.map(createEntry)); },
      get(arg) {
        const index = getIndex(arg);

        return (index < 0 || index >= items.length) ? undefined : items[index].value;
      },
      put(key, value) {
        const index = entryLookupMap[key] ?? -1;

        if (index < 0) {
          collection.add({ key, value });
        } else {
          collection.update(index, { key, value });
        }
      },
      remove(arg) {
        if (typeof arg === 'number') {
          collection.remove(arg);
        } else {
          collection.remove(entryLookupMap[isEntry(arg) ? arg.key : arg] ?? -1);
        }
      },
      clear: collection.clear,
    };
  }, [collection, entryLookupMap, getIndex, items]);
}

function initialize<T>(initializer: (T[] | Entry<T>[] | (() => T[] | Entry<T>[]))) {
  const initialItems = typeof initializer === 'function' ? initializer() : initializer;

  if (isEntryArray(initialItems)) return initialItems;

  return initialItems.map(createEntry);
}

type KeyedCollectionResult<T> = [Entry<T>[], KeyedCollectionDispatch<T>] & KeyedCollectionDispatch<T> & { items: Entry<T>[] };

/**
 * Hook which provides a collection of items that can be looked up by keys and aded with keys
 * It is order stable.
 *
 * @example
 * const [ words, wordCollection ] = useKeyedCollection<string>();
 *
 * wordCollection.add('asdf@example.com'); // [ { key: '314gx_r23d', value: 'asdf@example.com' } ]
 * wordCollection.get('314gx_r23d'); // 'asdf@example.com'
 * wordCollection.get(0); // 'asdf@example.com'
 * wordCollection.get(words[0]); // 'asdf@example.com'
 * wordCollection.put('emailAddress', 'asdf@example.com'); // [ { key: 'emailAddress', value: 'asdf@example.com' } ]
 * wordCollection.remove('314gx_r23d'); // []
 * wordCollection.remove(0); // []
 * wordCollection.remove(words[0]); // []
 *
 * @param initializer initial items of the collection, if its an Entry the keys will be preserved, if its an object without `key` new keys will be generated
 */
export function useKeyedCollection<T>(): KeyedCollectionResult<T>;
export function useKeyedCollection<T>(initializer: (Entry<T>[] | (() => Entry<T>[]))): KeyedCollectionResult<T>;
export function useKeyedCollection<T>(initializer: (T[] | (() => T[]))): KeyedCollectionResult<T>;
export function useKeyedCollection<T>(initializer: (T[] | Entry<T>[] | (() => T[] | Entry<T>[])) = []): KeyedCollectionResult<T> {
  const [items, dispatch] = useCollection(() => initialize(initializer));

  const collection = useKeyedCollectionDispatch(items, dispatch);

  return Object.assign([items, collection], { items, ...collection }) as KeyedCollectionResult<T>;
}

function createKeyedStorageCollectionHook(useHook: (typeof useLocalStorageCollection & typeof useLocalStorageCollection)) {
  /**
   * Hook which provides a collection of items that can be looked up by keys and synced to window storage
   * It is order stable.
   *
   * @example
   * const [ words, wordCollection ] = useKeyedStorageCollection<string>(SessionStorage, 'words');
   *
   * wordCollection.add('asdf@example.com'); // [ { key: '314gx_r23d', value: 'asdf@example.com' } ]
   * wordCollection.get('314gx_r23d'); // 'asdf@example.com'
   * wordCollection.get(0); // 'asdf@example.com'
   * wordCollection.get(words[0]); // 'asdf@example.com'
   * wordCollection.put('emailAddress', 'asdf@example.com'); // [ { key: 'emailAddress', value: 'asdf@example.com' } ]
   * wordCollection.remove('314gx_r23d'); // []
   * wordCollection.remove(0); // []
   * wordCollection.remove(words[0]); // []
   *
   * @param key {string} key where value is stored in storage
   * @param initializer initial items of the collection, if its an Entry the keys will be preserved, if its an object without `key` new keys will be generated
   */
  function useKeyedStorageCollection<T>(key: string | undefined): KeyedCollectionResult<T>;
  function useKeyedStorageCollection<T>(key: string | undefined, initializer: (Entry<T>[] | (() => Entry<T>[]))): KeyedCollectionResult<T>;
  function useKeyedStorageCollection<T>(key: string | undefined, initializer: (T[] | (() => T[]))): KeyedCollectionResult<T>;
  function useKeyedStorageCollection<T>(key: string | undefined, initializer: (T[] | Entry<T>[] | (() => T[] | Entry<T>[])) = []): KeyedCollectionResult<T> {
    const [items, dispatch] = useHook(key, () => initialize(initializer));

    const collection = useKeyedCollectionDispatch(items, dispatch);

    return Object.assign([items, collection], { items, ...collection }) as KeyedCollectionResult<T>;
  }

  return useKeyedStorageCollection;
}

export const useSessionStorageKeyedCollection = createKeyedStorageCollectionHook(useSessionStorageCollection);
export const useLocalStorageKeyedCollection = createKeyedStorageCollectionHook(useLocalStorageCollection);
